// Surface water flow over paper, carrying suspended pigment.
//
// Model: thin-film (lubrication) flow. Watercolour films are a fraction of a
// millimetre deep and slow, so inertia is negligible and the volume flux is
//     q = -(h^3 / 3 mu) * rho g * grad(eta - tilt . x)
// where h is water depth and eta = h + surface elevation (paper relief, dried
// paint and wet paint body). We fold rho g / 3 mu into one mobility constant.
// The h^3 dependence is what makes thin films nearly immobile while pooled
// water runs freely.
//
// Contact-line pinning: surface water may advance onto a dry cell only if the
// upstream depth exceeds that cell's pinning threshold (paper sizing, varied
// per fibre). Pinned edges plus faster evaporation at the edge produce the
// outward capillary flow that deposits pigment at the boundary (coffee-ring
// effect, Deegan et al. 1997), which is how real watercolour edges darken.
//
// Pigment is advected conservatively with the water and also mixes by a small
// diffusion within connected wet water (wet-in-wet softening).

import { PIGMENT_SLOTS } from '../core/PaintState.js';
import { tileBounds, getScratch } from './region.js';
import { clearFaces, limitOutflow, carryAlongFaces, applyFaces } from './FluxTransport.js';

export const WATER_WET_DEPTH = 0.004;
export const PAPER_WET_SATURATION = 0.08;

export const DEFAULT_WATER_PARAMS = Object.freeze({
  mobility: 600, // rho g / 3 mu in engine units (water at sub-millimetre depths)
  mobilityDepthCap: 0.4, // depths above this flow as if this deep (keeps explicit steps stable; deep pools are fast regardless)
  reliefScale: 0.06, // paper relief amplitude in water-depth units
  pinDepth: 0.06, // depth needed to advance onto dry sized paper (scaled by surface.pinning)
  pigmentDiffusion: 0.02, // particle diffusion is small (high Peclet number); softening comes from flow
  maxSubstep: 0.05,
});

function isWet(state, i) {
  return state.water[i] > WATER_WET_DEPTH || state.paperSat[i] > PAPER_WET_SATURATION;
}

function maxWaterDepth(state, region) {
  let max = 0;
  for (const t of region.tiles) {
    const [x0, y0, x1, y1] = tileBounds(state, t);
    for (let y = y0; y < y1; y += 1) {
      for (let x = x0; x < x1; x += 1) {
        const h = state.water[y * state.width + x];
        if (h > max) max = h;
      }
    }
  }
  return max;
}

function computeElevation(state, region, params, eta) {
  const { width } = state;
  const relief = state.surface.heightField;
  for (const t of region.tiles) {
    const [x0, y0, x1, y1] = tileBounds(state, t);
    for (let y = y0; y < y1; y += 1) {
      for (let x = x0; x < x1; x += 1) {
        const i = y * width + x;
        eta[i] = state.water[i] + params.reliefScale * relief[i] + state.dryHeight[i] * 0.02 + state.bodyVol[i] * 0.02;
      }
    }
  }
}

function faceFlux(state, params, eta, i, j, tilt, dt) {
  const drive = eta[i] - eta[j] + tilt;
  if (drive === 0) return 0;
  const src = drive > 0 ? i : j;
  const dst = drive > 0 ? j : i;
  const h = state.water[src];
  if (h < 1e-6) return 0;
  if (!isWet(state, dst)) {
    const threshold = params.pinDepth * state.surface.pinning[dst];
    if (h < threshold) return 0;
  }
  const hc = h < params.mobilityDepthCap ? h : params.mobilityDepthCap;
  const amount = params.mobility * hc * hc * hc * Math.abs(drive) * dt;
  return drive > 0 ? amount : -amount;
}

export function stepWater(state, region, dt, env = {}) {
  const params = { ...DEFAULT_WATER_PARAMS, ...(env.water || {}) };
  const { width, height } = state;
  const n = width * height;
  const eta = getScratch(state, 'eta', n);
  const fx = getScratch(state, 'waterFx', n);
  const fy = getScratch(state, 'waterFy', n);
  const tiltX = env.tiltX || 0;
  const tiltY = env.tiltY || 0;

  const hMax = Math.min(params.mobilityDepthCap, maxWaterDepth(state, region));
  if (hMax < 1e-5) return 0;
  // Explicit stability for d/dt h = div(M h^3 grad h): dt * M * h^3 <= ~1/4 (dx = 1).
  const stable = Math.min(params.maxSubstep, 0.15 / (params.mobility * hMax * hMax * hMax + 1e-9));
  const substeps = Math.max(1, Math.min(64, Math.ceil(dt / stable)));
  const h = dt / substeps;

  for (let s = 0; s < substeps; s += 1) {
    computeElevation(state, region, params, eta);
    clearFaces(state, region, fx, fy);
    for (const t of region.tiles) {
      const [x0, y0, x1, y1] = tileBounds(state, t);
      for (let y = y0; y < y1; y += 1) {
        for (let x = x0; x < x1; x += 1) {
          const i = y * width + x;
          if (x + 1 < width) fx[i] = faceFlux(state, params, eta, i, i + 1, tiltX, h);
          if (y + 1 < height) fy[i] = faceFlux(state, params, eta, i, i + width, tiltY, h);
        }
      }
    }
    limitOutflow(state, region, fx, fy, state.water);
    for (let k = 0; k < PIGMENT_SLOTS; k += 1) {
      if (state.slots[k]) carryAlongFaces(state, region, fx, fy, state.water, state.wetPig[k]);
    }
    applyFaces(state, region, fx, fy, state.water);
    diffusePigment(state, region, params.pigmentDiffusion * h);
  }
  return substeps;
}

// Concentration diffusion between neighbouring cells that both hold water.
// Each face's exchange is computed from a snapshot of the old pigment, so the
// result does not depend on cell order (the GPU kernel does the same). The
// per-face limit of 1/8 of the source keeps the sum over four faces safe.
function diffusePigment(state, region, rate) {
  if (rate <= 0) return;
  const { width, height, water } = state;
  const old = getScratch(state, 'diffuseOld', width * height);
  for (let k = 0; k < PIGMENT_SLOTS; k += 1) {
    if (!state.slots[k]) continue;
    const g = state.wetPig[k];
    for (const t of region.tiles) {
      const [x0, y0, x1, y1] = tileBounds(state, t);
      for (let y = y0; y < y1; y += 1) for (let x = x0; x < x1; x += 1) old[y * width + x] = g[y * width + x];
    }
    for (const t of region.tiles) {
      const [x0, y0, x1, y1] = tileBounds(state, t);
      for (let y = y0; y < y1; y += 1) {
        for (let x = x0; x < x1; x += 1) {
          const i = y * width + x;
          if (water[i] < WATER_WET_DEPTH) continue;
          let delta = 0;
          if (x > 0) delta += diffusionFlux(old, water, i - 1, i, rate);
          if (x + 1 < width) delta -= diffusionFlux(old, water, i, i + 1, rate);
          if (y > 0) delta += diffusionFlux(old, water, i - width, i, rate);
          if (y + 1 < height) delta -= diffusionFlux(old, water, i, i + width, rate);
          g[i] = Math.max(0, old[i] + delta);
        }
      }
    }
  }
}

// Pigment moving from cell a to cell b across their shared face (negative = b to a).
export function diffusionFlux(g, water, a, b, rate) {
  const ha = water[a];
  const hb = water[b];
  if (ha < WATER_WET_DEPTH || hb < WATER_WET_DEPTH) return 0;
  const moved = rate * Math.min(ha, hb) * (g[a] / ha - g[b] / hb);
  return moved > 0 ? Math.min(moved, 0.125 * g[a]) : Math.max(moved, -0.125 * g[b]);
}

export { isWet };
